<template>
  <!-- Бегущая строка в шапке: объявление магазина (например, что приём заказов
       временно приостановлен). Текст, цвет и включённость правятся в админке.
       Рендерится и на сервере — объявление видно сразу, без ожидания скриптов -->
  <div
    v-if="visible"
    class="ticker"
    :style="{ backgroundColor: bg, color: fg }"
    role="status"
  >
    <!-- Две одинаковые половины: анимация сдвигает ленту ровно на одну из них,
         поэтому склейка незаметна и строка бежит без рывка -->
    <div class="ticker__track" :style="durationStyle">
      <div ref="groupEl" class="ticker__group">
        <span v-for="n in repeat" :key="`a${n}`" class="ticker__item">{{ text }}</span>
      </div>
      <div class="ticker__group" aria-hidden="true">
        <span v-for="n in repeat" :key="`b${n}`" class="ticker__item">{{ text }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
// Скорость ленты в пикселях в секунду. Небольшая — так строка читается
// спокойно и не мельтешит. Длительность анимации считаем от ширины текста,
// чтобы на любом объявлении скорость была одинаковой
const SPEED_PX_PER_SEC = 45;

const shopConfig = useShopConfig();

const notice = computed(
  () => shopConfig.value.notice ?? { enabled: false, text: "", color: "#e6a23c" }
);
const text = computed(() => (notice.value.text || "").trim());
const visible = computed(() => notice.value.enabled && text.value.length > 0);

const bg = computed(() => notice.value.color || "#e6a23c");

// Текст читаемого цвета подбираем по яркости фона: на тёмном — белый,
// на светлом — почти чёрный. Так админу достаточно выбрать один цвет
const fg = computed(() => {
  const hex = String(bg.value).replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex.slice(0, 6);
  const int = parseInt(full || "e6a23c", 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#303133" : "#ffffff";
});

// Короткий текст повторяем чаще, чтобы половина ленты была не уже экрана
const repeat = computed(() => (text.value.length > 80 ? 1 : text.value.length > 40 ? 2 : 3));

// Длительность одного прохода. До замера — разумное значение по длине текста,
// чтобы и в серверной разметке скорость была спокойной. После монтирования
// уточняем по реальной ширине, деля её на SPEED_PX_PER_SEC
const seconds = ref(Math.max(24, Math.round((text.value.length * repeat.value * 9) / SPEED_PX_PER_SEC)));
const durationStyle = computed(() => ({ animationDuration: `${seconds.value}s` }));

const groupEl = ref(null);

const recalc = () => {
  const width = groupEl.value?.scrollWidth || 0;
  if (!width) return;
  // Ограничиваем разумными пределами: совсем короткое — не слишком быстро,
  // очень длинное — не бесконечно медленно
  seconds.value = Math.min(90, Math.max(18, Math.round(width / SPEED_PX_PER_SEC)));
};

onMounted(recalc);
watch([text, repeat], () => nextTick(recalc));
</script>

<style scoped>
.ticker {
  overflow: hidden;
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.4;
  padding: 5px 0;
}

.ticker__track {
  display: flex;
  width: max-content;
  /* Скорость задаётся длительностью из скрипта; движение равномерное */
  animation-name: ticker-scroll;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-duration: 40s; /* запасное значение до замера */
  will-change: transform;
}

.ticker__group {
  display: flex;
  flex: none;
}

.ticker__item {
  padding: 0 32px;
  font-weight: 500;
}

/* Пауза при наведении — чтобы можно было дочитать */
.ticker:hover .ticker__track {
  animation-play-state: paused;
}

@keyframes ticker-scroll {
  from {
    transform: translateX(0);
  }
  to {
    /* Ровно одна половина ленты — вторая как раз встаёт на её место */
    transform: translateX(-50%);
  }
}

/* Уважаем системную настройку «меньше движения»: тогда просто статичный текст */
@media (prefers-reduced-motion: reduce) {
  .ticker__track {
    animation: none;
    justify-content: center;
    width: 100%;
  }
  .ticker__group:last-child {
    display: none;
  }
}
</style>
